import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "./ScreenContainer.css";

function ScreenContainer() {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfigs, setSortConfigs] = useState([]); // supports multi-column sorting
  const [executingSymbol, setExecutingSymbol] = useState(null);
  const token = localStorage.getItem("authToken");
  useEffect(() => {
    const fetchStocksAndLTP = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        };
        const stockRes = await axios.get(
          "  https://quatdashboard.com:8084/api/stocks/high-volume",
          config,
        );
        const ltpRes = await axios.get(
          "  https://quatdashboard.com:8084/api/stocks/high-volume/ltp",
          config,
        );

        const mergedData = stockRes.data
          .map((stock) => {
            const ltp = ltpRes.data[stock.symbol] || 0;

            // ✅ Move% (LTP position between low→high)
            // const moveFrom5Y =
            //   stock.high_5yr > stock.low_5yr
            //     ? ((ltp - stock.low_5yr) / (stock.high_5yr - stock.low_5yr)) *
            //       100
            //     : 0;

            // const moveFrom52W =
            //   stock.high_52w > stock.low_52w
            //     ? ((ltp - stock.low_52w) / (stock.high_52w - stock.low_52w)) *
            //       100
            const moveFrom5Y =
              ((stock.high_5yr - stock.low_5yr) / stock.low_5yr) * 100;

            const moveFrom52W =
              ((stock.high_52w - stock.low_52w) / stock.low_52w) * 100;
            //     : 0;

            const ltpFrom52WHigh =
              stock.high_52w > 0
                ? ((ltp - stock.high_52w) / stock.high_52w) * 100
                : 0;

            const ltpFrom5YHigh =
              stock.high_5yr > 0
                ? ((ltp - stock.high_5yr) / stock.high_5yr) * 100
                : 0;

            // ✅ Volume capture % (1W of 1M)
            const volumeDiff =
              stock.volume_1m > 0
                ? (stock.volume_1w / stock.volume_1m) * 100
                : 0;

            return {
              ...stock,
              ltp,
              moveFrom5Y,
              moveFrom52W,
              ltpFrom52WHigh,
              ltpFrom5YHigh,
              volumeDiff,
            };
          })
          .filter((s) => s.ltpFrom52WHigh > -5); // only within -5% from 52W high

        setStocks(mergedData);
      } catch (err) {
        console.error("❌ Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStocksAndLTP();
  }, []);

  // ✅ Handle explicit sorting by arrow clicks
  const requestSort = (key, direction, event) => {
    const isShiftPressed = event.shiftKey;

    setSortConfigs((prev) => {
      const existing = prev.find((c) => c.key === key);
      let newConfigs;

      if (existing) {
        existing.direction = direction; // directly set asc/desc
        newConfigs = prev.map((c) => (c.key === key ? existing : c));
      } else {
        newConfigs = [...prev, { key, direction }];
      }

      return isShiftPressed ? newConfigs : [{ key, direction }];
    });
  };

  const handleEntryClick = async (symbol) => {
    const confirmTrade = window.confirm(
      `Are you sure you want to execute trade for ${symbol}?`,
    );
    if (!confirmTrade) return;

    setExecutingSymbol(symbol);

    try {
      const response = await axios.post(
        `  https://quatdashboard.com:8084/api/stocks/execute-trade`,
        null,
        {
          params: { symbol },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      alert(`✅ Trade executed for ${symbol}: ${response.data}`);
    } catch (error) {
      console.error("❌ Error executing trade:", error);
      alert(`❌ Failed to execute trade for ${symbol}`);
    } finally {
      setExecutingSymbol(null);
    }
  };

  // ✅ Sort using useMemo
  const sortedStocks = useMemo(() => {
    if (sortConfigs.length === 0) return stocks;

    return [...stocks].sort((a, b) => {
      for (let config of sortConfigs) {
        const valA = a[config.key];
        const valB = b[config.key];
        if (valA < valB) return config.direction === "asc" ? -1 : 1;
        if (valA > valB) return config.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [stocks, sortConfigs]);

  // ✅ Render sorting arrows (▲ / ▼)
  const renderSortArrows = (key) => {
    const activeConfig = sortConfigs.find((c) => c.key === key);
    const activeDir = activeConfig?.direction;

    return (
      <span className="sort-arrows">
        <span
          className={`arrow up ${activeDir === "asc" ? "active" : ""}`}
          onClick={(e) => requestSort(key, "asc", e)}
        >
          ▲
        </span>
        <span
          className={`arrow down ${activeDir === "desc" ? "active" : ""}`}
          onClick={(e) => requestSort(key, "desc", e)}
        >
          ▼
        </span>
      </span>
    );
  };

  if (loading) return <div className="loader">Loading stocks...</div>;

  return (
    <div className="screen-container">
      <h2 className="screen-title">📊 Stocks Near 52-Week High (Within -5%)</h2>
      <p className="note">
        💡 Tip: Hold <b>Shift</b> while clicking arrows to sort by multiple
        columns.
      </p>

      {/* ✅ Add responsive wrapper here */}
      <div className="table-wrapper">
        <table className="stock-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>5Y High</th>
              <th>5Y Vol</th>
              <th>52W High</th>
              <th>52W Vol</th>
              <th>1M Vol</th>
              <th>1W Vol</th>
              <th>LTP</th>
              <th>Move% (5Y) {renderSortArrows("moveFrom5Y")}</th>
              <th>Move% (52W) {renderSortArrows("moveFrom52W")}</th>
              <th>
                MOVE% from (5Y to LTP) {renderSortArrows("ltpFrom5YHigh")}
              </th>
              <th>
                MOVE% from (52W to LTP) {renderSortArrows("ltpFrom52WHigh")}
              </th>
              <th>Vol% Captured (1W of 1M) {renderSortArrows("volumeDiff")}</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {sortedStocks.map((s, i) => (
              <tr key={i}>
                <td className="symbol">{s.symbol}</td>
                <td>{s.high_5yr?.toFixed(2)}</td>
                <td>{s.volume_5yr?.toLocaleString()}</td>
                <td>{s.high_52w?.toFixed(2)}</td>
                <td>{s.volume_52w?.toLocaleString()}</td>
                <td>{s.volume_1m?.toLocaleString()}</td>
                <td>{s.volume_1w?.toLocaleString()}</td>
                <td>{s.ltp?.toFixed(2)}</td>
                <td className={s.moveFrom5Y > 80 ? "positive" : "light-red"}>
                  {s.moveFrom5Y.toFixed(2)}%
                </td>
                <td className={s.moveFrom52W > 80 ? "positive" : "light-red"}>
                  {s.moveFrom52W.toFixed(2)}%
                </td>
                <td className={s.ltpFrom5YHigh > 0 ? "positive" : "green"}>
                  {s.ltpFrom5YHigh.toFixed(2)}%
                </td>
                <td className={s.ltpFrom52WHigh > 0 ? "positive" : "green"}>
                  {s.ltpFrom52WHigh.toFixed(2)}%
                </td>
                <td className={s.volumeDiff > 50 ? "positive" : "light-red"}>
                  {s.volumeDiff.toFixed(2)}%
                </td>
                <td>
                  <button
                    className="entry-btn"
                    disabled={executingSymbol === s.symbol}
                    onClick={() => handleEntryClick(s.symbol)}
                  >
                    {executingSymbol === s.symbol ? "Processing..." : "Entry"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ScreenContainer;
